import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, Send, Loader2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { Input } from "../../../components/ui/input";
import {
  useContractComments, useAddContractComment, useSubmitChangeRequest, useOpenContractDiscussion,
} from "@/lib/commercialQueries";
import { rrFetch } from "@/lib/rehearsalRoomAuth";

/**
 * Master v5 Prompt 14 — client-facing "Request changes": annotated
 * comments (page/section anchors, never mutating the original contract
 * FileAsset) OR "Message coach about changes" (opens the authorized
 * Prompt-13 thread, pre-fills its draft's context_link to this exact
 * ContractRecord, then hands off to the Messages page — never touches
 * TidyCal/calendar/session-proposal code).
 */
export function RequestChangesPanel({ workspaceId, contract }) {
  const navigate = useNavigate();
  const { data: commentsData } = useContractComments(workspaceId, contract.ctr_id);
  const addComment = useAddContractComment(workspaceId, contract.ctr_id);
  const submitChange = useSubmitChangeRequest(workspaceId);
  const openDiscussion = useOpenContractDiscussion(workspaceId);

  const [page, setPage] = useState(1);
  const [sectionLabel, setSectionLabel] = useState("");
  const [bodyText, setBodyText] = useState("");
  const comments = commentsData?.items || [];

  async function handleAddComment() {
    if (!bodyText.trim()) return;
    try {
      await addComment.mutateAsync({ page: Number(page) || 1, section_label: sectionLabel || null, body_text: bodyText.trim() });
      setBodyText(""); setSectionLabel("");
      toast.success("Comment added");
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't add that comment");
    }
  }

  async function handleSubmit() {
    try {
      await submitChange.mutateAsync(contract.ctr_id);
      toast.success("Change request submitted to your coach");
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't submit the change request");
    }
  }

  async function handleMessageCoach() {
    try {
      const { thread_id } = await openDiscussion.mutateAsync(contract.ctr_id);
      const draftRes = await rrFetch(`/api/rehearsal-room/workspaces/${workspaceId}/messages/threads/${thread_id}/draft`, {
        method: "PUT",
        body: JSON.stringify({ body_text: "", attachments: [], context_link: { target_type: "contract", target_id: contract.ctr_id, label: contract.title } }),
      });
      if (!draftRes.ok) {
        const body = await draftRes.json().catch(() => null);
        throw new Error(body?.detail || "draft_save_failed");
      }
      navigate(`/rehearsal-room/w/${workspaceId}/messages`, { state: { activeThreadId: thread_id } });
    } catch (e) {
      toast.error(typeof e.message === "string" && e.message !== "draft_save_failed" ? e.message : "Couldn't open a thread with your coach");
    }
  }

  return (
    <div className="border-t border-ink/10 pt-3 mt-3 space-y-3" data-testid="commercial-request-changes-panel">
      <p className="text-sm font-medium text-ink">Request changes</p>

      {comments.length > 0 && (
        <div className="space-y-1.5" data-testid="commercial-comments-list">
          {comments.map((c) => (
            <div key={c.ccm_id} className="text-xs bg-ink/[0.03] rounded px-2.5 py-1.5" data-testid={`commercial-comment-${c.ccm_id}`}>
              <span className="text-terracotta font-medium">Page {c.anchor.page}{c.anchor.section_label ? ` · ${c.anchor.section_label}` : ""}: </span>
              <span className="text-ink">{c.body_text}</span>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-[70px_1fr] gap-2">
        <Input type="number" min={1} value={page} onChange={(e) => setPage(e.target.value)} placeholder="Page" data-testid="commercial-comment-page-input" />
        <Input value={sectionLabel} onChange={(e) => setSectionLabel(e.target.value)} placeholder="Section (optional)" data-testid="commercial-comment-section-input" />
      </div>
      <Textarea
        value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={2}
        placeholder="What would you like changed here?" data-testid="commercial-comment-body-input"
      />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={handleAddComment} disabled={!bodyText.trim() || addComment.isPending} data-testid="commercial-add-comment-btn">
          {addComment.isPending ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : null} Add comment
        </Button>
        <Button size="sm" onClick={handleSubmit} disabled={submitChange.isPending} data-testid="commercial-submit-change-request-btn">
          {submitChange.isPending ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Send size={13} className="mr-1.5" />}
          Submit change request
        </Button>
        <Button size="sm" variant="ghost" onClick={handleMessageCoach} disabled={openDiscussion.isPending} data-testid="commercial-message-coach-btn">
          <MessageSquare size={13} className="mr-1.5" /> Message coach about changes
        </Button>
      </div>
    </div>
  );
}
